import { LEGACY_ORG_ROLE, PLATFORM_ROLE } from "@/utils/roles";

export type PeopleByRoleCounts = {
  admin: number;
  member: number;
  viewer: number;
};

type OrgUserLike = {
  id?: string;
  role?: string;
  platformRole?: string;
};

/**
 * Count org users by platform role bucket for the Admin Home card.
 * owner/admin → Admin; member → Member; viewer/guest → Viewer.
 */
export function countPeopleByRole(users: readonly OrgUserLike[]): PeopleByRoleCounts {
  const counts: PeopleByRoleCounts = { admin: 0, member: 0, viewer: 0 };
  for (const user of users) {
    const raw = String(user.platformRole ?? user.role ?? "")
      .trim()
      .toLowerCase();
    if (
      raw === LEGACY_ORG_ROLE.OWNER ||
      raw === LEGACY_ORG_ROLE.ADMIN ||
      raw === PLATFORM_ROLE.ADMIN.toLowerCase()
    ) {
      counts.admin += 1;
    } else if (
      raw === LEGACY_ORG_ROLE.VIEWER ||
      raw === PLATFORM_ROLE.VIEWER.toLowerCase() ||
      raw === "guest"
    ) {
      counts.viewer += 1;
    } else {
      counts.member += 1;
    }
  }
  return counts;
}

/** True when the directory includes someone other than the signed-in user. */
export function hasOtherOrgMember(
  users: readonly OrgUserLike[],
  currentUserId: string | undefined,
): boolean {
  if (!currentUserId) return users.length > 1;
  return users.some((u) => String(u.id ?? "") !== currentUserId);
}

/**
 * A roster is believable only if it lists the signed-in Admin. An org user list
 * that comes back empty, or without the person asking, cannot be the whole
 * organization — counting it would render "0 / 0 / 0" for an organization that
 * has at least one Admin. The caller shows Unavailable instead.
 */
export function rosterIncludesCurrentUser(
  users: readonly OrgUserLike[],
  currentUserId: string | undefined,
): boolean {
  if (!currentUserId) return false;
  return users.some((u) => String(u.id ?? "") === currentUserId);
}
